import React, { useState, useEffect } from 'react';
import { FaCog, FaCalendarAlt, FaCheckCircle, FaSpinner, FaBook, FaSave, FaClock, FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import { getApiUrl } from '../utils/config';
import { useToast } from '../utils/toastContext';
import { getActionErrorMessage } from '../utils/errorHandler';
import { buildCurriculumDateDefaults, splitDateRangeIntoRotations, getRotationRangeFromRotations } from '../utils/dateUtils';
import '../styles/SearchableDropdown.css';
import '../styles/ConfigurationFormModern.css';

const ConfigurationFormModern = () => {
  const [curricula, setCurricula] = useState([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState('');
  const [rotationWarning, setRotationWarning] = useState(null);
  const [config, setConfig] = useState(() => {
    const defaults = buildCurriculumDateDefaults(8);
    return {
      rotationsNumber: 8,
      choicesCount: 4,
      rotations: defaults.rotations,
      form_registration: defaults.form_registration,
      spep_prefs: false,
      sitePreceptorOnly: false,
      defaultFullTimeHrs: { start_time: '08:00', end_time: '16:00' },
      defaultPartTimeHrs: { start_time: '08:00', end_time: '12:00' },
      year: '',
      curriculum_id: ''
    };
  });
  const [isLoading, setIsLoading] = useState(true);
  const { showError, showSuccess } = useToast();

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(getApiUrl('config/get_configurations'));
      if (!response.ok) throw new Error('Failed to fetch configurations');
      
      const data = await response.json();
      const curriculaList = Object.keys(data).filter(key => !key.match(/^\d{4}$/));
      
      const curriculaObjects = curriculaList.sort().map(name => ({
        id: name,
        name: name
      }));
      
      setCurricula(curriculaObjects);
      
      if (curriculaList.length > 0) {
        setSelectedCurriculum(curriculaList[0]);
        await fetchConfiguration(curriculaList[0]);
      }
      setIsLoading(false);
    } catch (err) {
      showError(getActionErrorMessage(err, 'load configurations'));
      setIsLoading(false);
    }
  };

  const handleCurriculumChange = async (curriculum) => {
    setSelectedCurriculum(curriculum);
    if (curriculum) {
      await fetchConfiguration(curriculum);
    }
  };

  const fetchConfiguration = async (curriculum) => {
    try {
      setIsLoading(true);
      const response = await fetch(getApiUrl('config/get_configurations'));
      if (!response.ok) throw new Error('Failed to fetch configurations');
      
      const data = await response.json();
      const curriculumData = data[curriculum];

      if (curriculumData) {
        const parseDate = (dateValue) => {
          if (dateValue == null) return '';
          if (typeof dateValue === 'string') {
            const d = new Date(dateValue);
            return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
          }
          if (typeof dateValue === 'number' || dateValue instanceof Date) {
            const d = new Date(dateValue);
            return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
          }
          return '';
        };

        const parseNumber = (numberValue) => {
          if (numberValue && numberValue.$numberInt) {
            return parseInt(numberValue.$numberInt, 10);
          }
          return parseInt(numberValue, 10) || 0;
        };

        const rotationsCount = parseNumber(curriculumData.rotationsCount || curriculumData.rotationsNumber || 8);
        const rotationsData = curriculumData.rotations || [];
        const rotations = Array(rotationsCount).fill(null).map((_, index) => {
          const rotation = rotationsData[index];
          if (rotation && (rotation.startDate || rotation.endDate)) {
            return {
              startDate: parseDate(rotation.startDate),
              endDate: parseDate(rotation.endDate)
            };
          }
          return { startDate: '', endDate: '' };
        });
        
        const hasRegDates = curriculumData.form_registration?.startDate || curriculumData.form_registration?.endDate;
        
        setConfig({
          rotationsNumber: rotationsCount,
          choicesCount: parseNumber(curriculumData.choicesCount || 4),
          rotations: rotations,
          form_registration: hasRegDates ? {
            startDate: parseDate(curriculumData.form_registration?.startDate),
            endDate: parseDate(curriculumData.form_registration?.endDate)
          } : { startDate: '', endDate: '' },
          spep_prefs: curriculumData.spep_prefs || false,
          sitePreceptorOnly: curriculumData.sitePreceptorOnly || false,
          defaultFullTimeHrs: curriculumData.defaultFullTimeHrs || { start_time: '08:00', end_time: '16:00' },
          defaultPartTimeHrs: curriculumData.defaultPartTimeHrs || { start_time: '08:00', end_time: '12:00' },
          year: curriculumData.year || '',
          curriculum_id: curriculum
        });
      } else {
        const defaults = buildCurriculumDateDefaults(8);
        setConfig({
          rotationsNumber: 8,
          choicesCount: 4,
          rotations: defaults.rotations,
          form_registration: defaults.form_registration,
          spep_prefs: false,
          sitePreceptorOnly: false,
          defaultFullTimeHrs: { start_time: '08:00', end_time: '16:00' },
          defaultPartTimeHrs: { start_time: '08:00', end_time: '12:00' },
          year: '',
          curriculum_id: curriculum
        });
      }
      setIsLoading(false);
    } catch (err) {
      showError(getActionErrorMessage(err, 'load configuration'));
      setIsLoading(false);
    }
  };

  const submitConfiguration = async (confirmConfig = false) => {
    const transformDate = (dateString) => {
      if (!dateString) return null;
      return new Date(dateString).toISOString();
    };

    const payload = {
      curriculum_id: selectedCurriculum,
      rotationsNumber: config.rotationsNumber,
      choicesNumber: config.choicesCount,
      rotations: config.rotations.map(rot => ({
        startDate: transformDate(rot.startDate),
        endDate: transformDate(rot.endDate)
      })),
      form_registration: {
        startDate: transformDate(config.form_registration?.startDate),
        endDate: transformDate(config.form_registration?.endDate)
      },
      spep_prefs: config.spep_prefs,
      sitePreceptorOnly: config.sitePreceptorOnly,
      defaultFullTimeHrs: config.defaultFullTimeHrs,
      defaultPartTimeHrs: config.defaultPartTimeHrs,
      year: config.year
    };

    const params = new URLSearchParams({
      rotationsNumber: String(config.rotationsNumber),
      choicesNumber: String(config.choicesCount)
    });

    if (confirmConfig) {
      params.set('confirm', 'true');
    }

    const response = await fetch(
      getApiUrl(`config/set_configurations_by_curriculum/${selectedCurriculum}?${params.toString()}`),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    const text = await response.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    if (data?.status === 'warning' || data?.requires_confirmation) {
      setRotationWarning(data);
      return false;
    }

    if (!response.ok) {
      throw new Error(data?.detail || data?.message || text || 'Failed to update configuration');
    }

    return true;
  };

  const handleSubmit = async (e, confirmConfig = false) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setRotationWarning(null);

      const saved = await submitConfiguration(confirmConfig);
      if (!saved) {
        setIsLoading(false);
        return;
      }
      
      showSuccess(confirmConfig ? 'Configuration updated successfully.' : 'Configuration updated successfully!');
      setIsLoading(false);
    } catch (err) {
      showError(getActionErrorMessage(err, 'update configuration'));
      setIsLoading(false);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        newConfig[parent] = {
          ...prev[parent],
          [child]: value
        };
      } else {
        newConfig[field] = value;
        
        if (field === 'rotationsNumber') {
          const newCount = Math.max(1, Math.min(12, parseInt(value) || 1));
          const rotationRange = getRotationRangeFromRotations(prev.rotations);
          newConfig.rotationsNumber = newCount;
          newConfig.rotations = rotationRange
            ? splitDateRangeIntoRotations(rotationRange.startDate, rotationRange.endDate, newCount)
            : buildCurriculumDateDefaults(newCount).rotations;
        }
      }
      
      return newConfig;
    });
  };

  const handleRotationChange = (index, field, value) => {
    setConfig(prev => {
      const newRotations = [...prev.rotations];
      newRotations[index] = {
        ...newRotations[index],
        [field]: value
      };
      return {
        ...prev,
        rotations: newRotations
      };
    });
  };

  const registration = config.form_registration || { startDate: '', endDate: '' };

  if (isLoading && curricula.length === 0) {
    return (
      <div className="cfg-page">
        <div className="cfg-shell">
          <LoadingSpinner text="Loading configurations..." variant="page" />
        </div>
      </div>
    );
  }

  return (
    <div className="cfg-page">
      <div className="cfg-shell">
        <div className="cfg-hero">
          <div className="cfg-hero__titleRow">
            <div className="cfg-hero__icon">
              <FaCog />
            </div>
            <h1 className="cfg-hero__title">Configuration Management</h1>
          </div>
          <p className="cfg-hero__subtitle">
            Configure curriculum-based settings for rotations, choices, and form registration periods
          </p>
        </div>

        <div className="cfg-grid">
          <div className="cfg-card">
            <div className="cfg-card__head">
              <div className="cfg-card__icon cfg-card__icon--blue">
                <FaBook />
              </div>
              <div>
                <h3 className="cfg-card__title">Select Curriculum</h3>
                <p className="cfg-card__subtitle">Choose a curriculum to configure</p>
              </div>
            </div>

            <div>
              <SearchableDropdown
                options={curricula}
                value={selectedCurriculum}
                onChange={handleCurriculumChange}
                optionLabel="name"
                optionValue="id"
                placeholder="Select a curriculum..."
                disabled={isLoading}
                size="medium"
              />
            </div>
          </div>

          <form onSubmit={e => handleSubmit(e, false)} className="cfg-form">
            <div className="cfg-card">
              <div className="cfg-card__head">
                <div className="cfg-card__icon cfg-card__icon--navy">
                  <FaCheckCircle />
                </div>
                <div>
                  <h3 className="cfg-card__title">Basic Configuration</h3>
                  <p className="cfg-card__subtitle">Rotations and choice limits</p>
                </div>
              </div>

              <div className="cfg-fieldGrid">
                <div className="cfg-field">
                  <label>Number of Rotations</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={config.rotationsNumber}
                    onChange={(e) => handleConfigChange('rotationsNumber', parseInt(e.target.value) || 1)}
                    className="cfg-input"
                  />
                </div>
                <div className="cfg-field">
                  <label>Number of Choices per Rotation</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={config.choicesCount}
                    onChange={(e) => handleConfigChange('choicesCount', parseInt(e.target.value))}
                    className="cfg-input"
                  />
                </div>
              </div>

              <div className="cfg-toggleGrid">
                <label className="cfg-toggle">
                  <input
                    type="checkbox"
                    checked={config.spep_prefs}
                    onChange={(e) => handleConfigChange('spep_prefs', e.target.checked)}
                  />
                  <span>Enable SPEP Preferences</span>
                </label>
                <label className="cfg-toggle">
                  <input
                    type="checkbox"
                    checked={config.sitePreceptorOnly}
                    onChange={(e) => handleConfigChange('sitePreceptorOnly', e.target.checked)}
                  />
                  <span>Site Preceptor Only</span>
                </label>
              </div>
            </div>

            <div className="cfg-card">
              <div className="cfg-card__head">
                <div className="cfg-card__icon cfg-card__icon--purple">
                  <FaCalendarAlt />
                </div>
                <div>
                  <h3 className="cfg-card__title">Rotation Dates</h3>
                  <p className="cfg-card__subtitle">Set the start/end dates for each rotation</p>
                </div>
              </div>

              <div style={{ display: 'grid', gap: '0.85rem' }}>
                {config.rotations.map((rotation, index) => (
                  <div key={index} className="cfg-rotationRow">
                    <h4 className="cfg-rotationTitle">Rotation {index + 1}</h4>
                    <div className="cfg-rotationGrid">
                      <div className="cfg-field">
                        <label>Start Date</label>
                        <input
                          type="date"
                          value={rotation.startDate}
                          onChange={(e) => handleRotationChange(index, 'startDate', e.target.value)}
                          className="cfg-input"
                        />
                      </div>
                      <div className="cfg-field">
                        <label>End Date</label>
                        <input
                          type="date"
                          value={rotation.endDate}
                          onChange={(e) => handleRotationChange(index, 'endDate', e.target.value)}
                          className="cfg-input"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cfg-card">
              <div className="cfg-card__head">
                <div className="cfg-card__icon cfg-card__icon--green">
                  <FaClock />
                </div>
                <div>
                  <h3 className="cfg-card__title">Form Registration Window</h3>
                  <p className="cfg-card__subtitle">Set when users can submit preferences</p>
                </div>
              </div>

              <div className="cfg-fieldGrid" style={{ alignItems: 'end' }}>
                <div className="cfg-field">
                  <label>Registration Opens</label>
                  <input
                    type="date"
                    value={registration.startDate}
                    onChange={(e) => handleConfigChange('form_registration.startDate', e.target.value)}
                    className="cfg-input"
                  />
                </div>
                <div className="cfg-arrowHint">
                  <FaArrowRight />
                </div>
                <div className="cfg-field">
                  <label>Registration Closes</label>
                  <input
                    type="date"
                    value={registration.endDate}
                    onChange={(e) => handleConfigChange('form_registration.endDate', e.target.value)}
                    className="cfg-input"
                  />
                </div>
              </div>
            </div>

            <div className="cfg-actionRow">
              <button
                type="submit"
                disabled={isLoading || !selectedCurriculum}
                className="cfg-primaryBtn"
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="mc-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </form>

          {rotationWarning && (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(9, 26, 43, 0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem', zIndex: 2000,
            }}>
              <div style={{
                width: 'min(620px, 100%)', background: 'linear-gradient(180deg,#ffffff 0%,#f7fbff 100%)',
                borderRadius: 20, overflow: 'hidden',
                border: '1px solid rgba(193, 212, 233, 0.95)',
                boxShadow: '0 14px 34px rgba(22, 54, 86, 0.12)'
              }}>
                <div style={{ padding: '1.35rem 1.5rem', background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', borderBottom: '1px solid rgba(202,138,4,0.22)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.22)', color: '#7c2d12', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem', fontWeight: 800, border: '1px solid rgba(255,255,255,0.35)' }}>
                      <FaExclamationTriangle />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7c2d12' }}>Confirm rotation reduction</div>
                      <h3 style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', color: '#4a2508' }}>Student assignments exist in rotations that will be removed</h3>
                    </div>
                  </div>
                </div>

                <div style={{ padding: '1.4rem 1.5rem 1.5rem' }}>
                  <p style={{ margin: '0 0 1rem', color: '#475569', lineHeight: 1.6 }}>
                    {rotationWarning.warning || 'Reducing the rotation count would remove existing assigned slots.'}
                  </p>

                  {rotationWarning.conflict && (
                    <div style={{ background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)', border: '1px solid rgba(193, 212, 233, 0.9)', borderRadius: 16, padding: '1rem 1.1rem', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.55rem' }}>Example impact</div>
                      <div style={{ display: 'grid', gap: '0.5rem', color: '#15324f', fontSize: '0.94rem' }}>
                        <div><strong>Student:</strong> {rotationWarning.conflict.student_name || rotationWarning.conflict.student_id}</div>
                        <div><strong>Deleted rotations:</strong> {Array.isArray(rotationWarning.conflict.conflict_rotations) ? rotationWarning.conflict.conflict_rotations.map(r => `R${r}`).join(', ') : '—'}</div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setRotationWarning(null)}
                      disabled={isLoading}
                      style={{
                        padding: '0.8rem 1.2rem', borderRadius: 12, border: '1px solid rgba(193, 212, 233, 0.95)', background: 'white',
                        color: '#355a80', fontWeight: 700, cursor: isLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Go back
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setRotationWarning(null);
                        await handleSubmit({ preventDefault() {} }, true);
                      }}
                      disabled={isLoading}
                      style={{
                        padding: '0.8rem 1.3rem', borderRadius: 12, border: 'none',
                        background: 'linear-gradient(135deg,#dc2626,#ef4444)', color: 'white', fontWeight: 800,
                        cursor: isLoading ? 'not-allowed' : 'pointer', boxShadow: '0 10px 22px rgba(220,38,38,0.22)'
                      }}
                    >
                      {isLoading ? <><FaSpinner className="mc-spin" /> Saving...</> : 'Confirm and save anyway'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurationFormModern;